import { useState, useEffect, useRef } from "react";
import "@/App.css";
import emailjs from "@emailjs/browser";
import { 
  Menu, 
  X, 
  Phone, 
  MapPin, 
  Mail, 
  ChevronDown,
  Building2,
  Factory,
  Home,
  Route,
  ClipboardList,
  Hammer,
  Target,
  Shield,
  Users,
  Eye,
  Sparkles,
  ArrowRight
} from "lucide-react";

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_1gl8n6i";
const EMAILJS_TEMPLATE_ID = "template_mka24pa";
const EMAILJS_PUBLIC_KEY = "ry9XqXEq0kCYOr3Hq";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bestpoke-web/artifacts/2377c1jk_BESTPOKE%20PNG.png";
const LOGO_SVG = "https://customer-assets.emergentagent.com/job_bestpoke-web/artifacts/u2ce15cn_Bestpoke%20SVG.svg";
const HERO_BG = "https://images.pexels.com/photos/12049017/pexels-photo-12049017.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Services Data
const services = [
  {
    id: 1,
    title: "Arquitectura e Ingeniería",
    description: "Diseño y cálculo de estructuras funcionales y estéticas, integrando paisaje y urbanismo.",
    icon: Building2,
    image: "https://images.pexels.com/photos/32231937/pexels-photo-32231937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    id: 2,
    title: "Construcción Industrial y Comercial",
    description: "Ejecución de naves industriales, plantas y complejos de servicios, cumpliendo normativas técnicas.",
    icon: Factory,
    image: "https://customer-assets.emergentagent.com/job_bestpoke-web/artifacts/uowyfc24_industrial-comercial.jpg"
  },
  {
    id: 3,
    title: "Vivienda Unifamiliar y Multifamiliar",
    description: "Construcción de hogares y desarrollos verticales, enfocados en habitabilidad y acabados de alta gama.",
    icon: Home,
    image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    id: 4,
    title: "Obras de Urbanización",
    description: "Desarrollo de infraestructura base, vialidades e instalaciones eléctricas complejas.",
    icon: Route,
    image: "https://customer-assets.emergentagent.com/job_bestpoke-web/artifacts/o1owarbr_urbanizacion.jpg"
  },
  {
    id: 5,
    title: "Administración y Supervisión",
    description: "Gestión integral de recursos y procesos para mantener la ejecución fiel al presupuesto.",
    icon: ClipboardList,
    image: "https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    id: 6,
    title: "Acabados y Equipamiento",
    description: "Trabajos detallados de albañilería, carpintería e instalaciones finales de alta calidad.",
    icon: Hammer,
    image: "https://images.pexels.com/photos/33685849/pexels-photo-33685849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

// Strengths Data
const strengths = [
  {
    id: 1,
    title: "Enfoque Integral 360°",
    description: "Gestión completa desde el diseño arquitectónico hasta el equipamiento final, sin intermediarios.",
    icon: Target
  },
  {
    id: 2,
    title: "Proyectos de Alta Complejidad",
    description: "Experiencia probada en naves industriales y centros comerciales que requieren ingeniería de precisión.",
    icon: Factory
  },
  {
    id: 3,
    title: "Personalización Bespoke",
    description: "Adaptación de cada solución a las necesidades específicas de inversión y funcionalidad del cliente.",
    icon: Users
  },
  {
    id: 4,
    title: "Control Riguroso de Obra",
    description: "Supervisión constante para asegurar la fidelidad entre el proyecto ejecutivo y la construcción.",
    icon: Shield
  },
  {
    id: 5,
    title: "Dualidad Estética-Estructural",
    description: "Equilibrio perfecto entre diseños arquitectónicos de vanguardia y solidez estructural inmejorable.",
    icon: Sparkles
  }
];

// Navigation Links
const navLinks = [
  { href: "#inicio", label: "Inicio" },
  { href: "#empresa", label: "Empresa" },
  { href: "#servicios", label: "Servicios" },
  { href: "#fortalezas", label: "Fortalezas" },
  { href: "#contacto", label: "Contacto" }
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass" : "bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" onClick={(e) => handleNavClick(e, "#inicio")} data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="BESTPOKE" 
              className="h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-sm font-semibold text-brand-purple hover:text-brand-orange transition-colors uppercase tracking-wider"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-brand-purple"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden mobile-menu absolute top-20 left-0 right-0 border-t border-gray-100">
          <div className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="text-lg font-semibold text-brand-purple hover:text-brand-orange transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector("#contacto");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      id="inicio" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 text-center pt-20">
        <p className="overline mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
          <img 
            src={LOGO_SVG} 
            alt="BESTPOKE Logo" 
            className="h-32 sm:h-40 lg:h-48 w-auto mx-auto object-contain mb-4 brightness-0 invert"
          />
          <span className="block text-white text-lg sm:text-xl lg:text-2xl tracking-[0.15em] font-medium">
            ARTE Y CONSTRUCCIÓN BESTPOKE
          </span>
        </p>
        
        <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-8 opacity-0 animate-fade-in-up" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
          Donde el arte se construye<br />
          <span className="text-brand-orange">y la precisión se habita</span>
        </h1>
        
        <p className="text-white text-lg sm:text-xl max-w-2xl mx-auto mb-12 opacity-0 animate-fade-in-up" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>
          La exclusividad de lo hecho a mano, la solidez de lo bien construido.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up" style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}>
          <button 
            onClick={scrollToContact}
            data-testid="hero-cta-button"
            className="btn-primary-hero flex items-center justify-center gap-2"
          >
            Solicitar Cotización
            <ArrowRight size={18} />
          </button>
          <a 
            href="#servicios"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="hero-services-button"
            className="btn-secondary-hero flex items-center justify-center gap-2"
          >
            Ver Servicios
          </a>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={32} className="text-brand-orange" />
        </div>
      </div>
    </section>
  );
};

// Company Section
const CompanySection = () => {
  return (
    <section id="empresa" data-testid="company-section" className="py-24 sm:py-32 section-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column */}
          <div>
            <p className="overline mb-4">Nuestra Empresa</p>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-purple mb-6">
              Ingeniería y construcción integral
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              Somos una firma de ingeniería y construcción que materializa proyectos desde la concepción arquitectónica hasta la entrega de llave en mano.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Especializados en infraestructura de alta complejidad, incluyendo vivienda unifamiliar y multifamiliar, naves industriales y centros comerciales. Garantizamos altos estándares de seguridad y funcionalidad en cada etapa del proceso constructivo.
            </p>
          </div>

          {/* Right Column - Mission & Vision */}
          <div className="space-y-8">
            {/* Mission */}
            <div className="strength-card p-8 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="icon-container flex-shrink-0">
                  <Target size={24} className="text-brand-orange" />
                </div>
                <div>
                  <h3 className="font-outfit text-xl font-bold text-brand-purple mb-3">Misión</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Liderar el sector constructivo mediante soluciones de ingeniería personalizadas que optimicen recursos y aseguren la máxima calidad en cada obra de urbanización y edificación.
                  </p>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="strength-card p-8 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="icon-container flex-shrink-0">
                  <Eye size={24} className="text-brand-orange" />
                </div>
                <div>
                  <h3 className="font-outfit text-xl font-bold text-brand-purple mb-3">Visión</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Ser la constructora referente a nivel nacional, reconocida por ejecutar proyectos industriales y comerciales de gran escala con innovación constante, transformando el entorno urbano de manera sostenible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="servicios" data-testid="services-section" className="py-24 sm:py-32 section-light">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <p className="overline mb-4">Nuestros Servicios</p>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-purple mb-6">
            Soluciones integrales de construcción
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="service-card min-h-[420px] flex flex-col justify-end group rounded-xl"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 service-image bg-cover bg-center rounded-xl"
                  style={{ backgroundImage: `url(${service.image})` }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent rounded-xl group-hover:from-brand-purple/90 group-hover:via-brand-purple/50 transition-all duration-500" />
                
                {/* Content */}
                <div className="relative z-10 p-8">
                  <div className="icon-container mb-4 bg-white/20 border-white/30">
                    <IconComponent size={24} className="text-white" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Strengths Section
const StrengthsSection = () => {
  return (
    <section id="fortalezas" data-testid="strengths-section" className="py-24 sm:py-32 section-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <p className="overline mb-4">Por Qué Elegirnos</p>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-purple mb-6">
            Nuestras Fortalezas
          </h2>
          <div className="section-divider mx-auto mb-8" />
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            El impacto de BESTPOKE no es solo visual; es emocional y financiero. Un proyecto bien ejecutado es una inversión patrimonial.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {strengths.map((strength) => {
            const IconComponent = strength.icon;
            return (
              <div 
                key={strength.id}
                data-testid={`strength-card-${strength.id}`}
                className="strength-card p-8 rounded-xl"
              >
                <div className="icon-container mb-6">
                  <IconComponent size={24} className="text-brand-orange" />
                </div>
                <h3 className="font-outfit text-lg font-bold text-brand-purple mb-3">
                  {strength.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {strength.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          message: formData.message,
          to_name: "BESTPOKE"
        },
        EMAILJS_PUBLIC_KEY
      );

      setStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 sm:py-32 section-light">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div>
            <p className="overline mb-4">Contáctanos</p>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-purple mb-6">
              Iniciemos tu proyecto
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-12">
              Estamos listos para transformar tus ideas en realidad. Contáctanos y descubre cómo podemos ayudarte a construir el futuro.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="icon-container flex-shrink-0">
                  <MapPin size={24} className="text-brand-orange" />
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-brand-purple mb-1">Dirección</h4>
                  <p className="text-gray-600">
                    Calle Abogados No.638 Int. 1,<br />
                    Col. Jardines de Guadalupe,<br />
                    CP. 45030, Zapopan, Jalisco, México.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="icon-container flex-shrink-0">
                  <Phone size={24} className="text-brand-orange" />
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-brand-purple mb-1">Teléfono</h4>
                  <a href="tel:+523334754998" className="text-gray-600 hover:text-brand-orange transition-colors">
                    33 3475 4998
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="icon-container flex-shrink-0">
                  <Mail size={24} className="text-brand-orange" />
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-brand-purple mb-1">Email</h4>
                  <a href="mailto:contacto@bestpoke.com.mx" className="text-gray-600 hover:text-brand-orange transition-colors">
                    contacto@bestpoke.com.mx
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="strength-card p-8 sm:p-12 rounded-xl">
            <h3 className="font-outfit text-2xl font-bold text-brand-purple mb-8">
              Envíanos un mensaje
            </h3>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nombre completo"
                  required
                  data-testid="contact-name-input"
                  className="form-input"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Correo electrónico"
                  required
                  data-testid="contact-email-input"
                  className="form-input"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Teléfono"
                  required
                  data-testid="contact-phone-input"
                  className="form-input"
                />
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntanos sobre tu proyecto..."
                  required
                  rows={4}
                  data-testid="contact-message-input"
                  className="form-input resize-none"
                />
              </div>

              {status === "success" && (
                <div data-testid="form-success-message" className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
                  ¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.
                </div>
              )}

              {status === "error" && (
                <div data-testid="form-error-message" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="btn-primary w-full flex items-center justify-center gap-2 rounded-lg"
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Mensaje
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="footer-light py-12">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Logo */}
          <img 
            src={LOGO_URL} 
            alt="BESTPOKE" 
            className="h-10 w-auto object-contain"
          />

          {/* Copyright */}
          <p className="text-gray-500 text-sm text-center">
            © {currentYear} Arte y Construcción BESTPOKE. Todos los derechos reservados.
          </p>

          {/* Phone */}
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Phone size={16} className="text-brand-orange" />
            <a href="tel:+523334754998" className="hover:text-brand-orange transition-colors">33 3475 4998</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Initialize EmailJS
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  return (
    <div className="App bg-[#FAFAFA] min-h-screen" data-testid="app-container">
      <Navigation />
      <main>
        <HeroSection />
        <CompanySection />
        <ServicesSection />
        <StrengthsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
